import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { RoutePartsService } from "./services/route-parts/route-parts.service";
import { ChatterService } from './chat/shared/services/chatter.service';
import { AlertDialogComponent } from './views/admin/alert-dialog/alert-dialog.component';
import { AlertService } from './services/alert.service';
import { ReleaseNotesService, RELEASE_NOTES_FEATURE_CODE } from './services/release-notes.service';
import { openReleaseNotesDialog } from './components/common/release-notes-dialog/release-notes-dialog';
import { FeatureFlagService } from './core/feature-flags/feature-flags.service';
import { AuthService } from './services/auth/auth.service';
import { SupportContextService } from './core/support/support-context.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit {

  appTitle = 'Pinnacle Power';
  pageTitle = '';

  private readonly SUPPORT_BASE = environment.supportUrl;

  constructor(
    public title: Title,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private chatter: ChatterService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private releaseNotesService: ReleaseNotesService,
    private routePartsService: RoutePartsService,
    public featureFlags: FeatureFlagService,
    private authService: AuthService,
    private supportCtx: SupportContextService,) {
  }

  ngOnInit() {
    this.changePageTitle();

    this.chatter.dashAlert.subscribe((retval) => {
      console.log("Dash Alert update", retval);
      this.readNotifications();
    });
      this.readNotifications();
      this.checkReleaseNotes();
  }

  /**
   * Release notes popup — fires once per fresh sign-in, not on every reload.
   *
   * AuthService stamps `firstLogin` right before its post-login
   * window.location.replace(); we consume it here on the very next boot. The
   * server decides whether there is anything to show (it returns null once the
   * user ticked "don't show this again" for the published version).
   */
  private checkReleaseNotes() {
    if (!localStorage.getItem('sr_session_id')) return;
    if (!localStorage.getItem('firstLogin')) return;
    // Consume the flag before anything can bail out, so a disabled feature or a
    // failed call can't leave a stale firstLogin to fire on a later refresh.
    localStorage.removeItem('firstLogin');

    // Unregistered codes resolve to off, so the popup stays dark until the
    // REL_NOTES flag row exists and is enabled for this environment.
    if (!this.featureFlags.isEnabled(RELEASE_NOTES_FEATURE_CODE)) return;

    this.releaseNotesService.getCurrent().subscribe({
      next: (note) => {
        if (!note) return;
        openReleaseNotesDialog(this.dialog, { note });
      },
      error: (err) => console.error('Error loading release notes:', err),
    });
  }

  private readNotifications() {
    let sessionID = localStorage.getItem("sr_session_id");
    if(!sessionID) return;
    this.alertService.getOutstandingAlerts().subscribe((retval) => {
      if (!retval) return;
      for (let alert of retval) {
        //console.log('Opening alert dialog for alert:', alert);
        this.dialog.open(AlertDialogComponent, {
          width: '720px',
          data: alert,
        });
      }
    });
  }

  openSupportTicket(): void {
    const currentUrl = this.router.url;
  
    let ctx = this.supportCtx.getContext();

    const w = window.open(
      `${this.SUPPORT_BASE}/support?origin=${encodeURIComponent(ctx.origin || 'Pinnacle SiteReview')}`,
      'aiticket_pinnacle',
      'width=1280,height=860,left=100,top=60'
    );
  
    if (!w) {
      console.warn('[Support] Popup blocked by browser');
      return;
    }
  
    const timeout = setTimeout(() => {
      console.warn('[Support] Timeout — AI_TICKET_READY not received');
      window.removeEventListener('message', handler);
    }, 15000);
  
    const handler = (event: MessageEvent) => {
      if (event.source !== w || event.data?.type !== 'AI_TICKET_READY') return;
  
      clearTimeout(timeout);
      window.removeEventListener('message', handler);
  
      const user = this.authService.currentUser;
      const oauth2 = JSON.parse(localStorage.getItem('oauth2') || '{}');
  
      let supportOrigin = '*'; 
      try {
        supportOrigin = new URL(this.SUPPORT_BASE).origin;
      } catch (e) {
        console.warn('[Support] Could not parse supportOrigin', e);
      }
  
      const payload = {
        type:        'AI_TICKET_META',
        accessToken: oauth2.access_token ?? '',
        idToken:     oauth2.id_token ?? localStorage.getItem('sr_id_token') ?? '',
        userInfo: {
          name:  user?.name  ?? '',
          email: user?.email ?? '',
        },
        origin:   ctx.origin   || 'Pinnacle SiteReview',
        category: ctx.category || 'General',
        tags:     ctx.tags     || [],
        meta: {
          'User':         user?.name ?? '',
          'Email':        user?.email ?? '',
          'Role':         user?.userLevel?.name ?? '',
          'Page URL':     window.location.href,
          'Page Title':   this.pageTitle,
          ...ctx.meta    
        }
      };
  
      console.log('Posting metadata to support window →', supportOrigin);
      console.log('Payload:', payload);
  
      //w.postMessage(payload, supportOrigin);
      w.postMessage(payload, '*');
    };
  
    window.addEventListener('message', handler);
  }

  // openSupportTicket(): void {
  //   //console.log('[Support] openSupportTicket called, base=', this.SUPPORT_BASE);
  //   const w = window.open(
  //     `${this.SUPPORT_BASE}/support?origin=Pinnacle+SiteReview`,
  //     'aiticket_pinnacle',
  //     'width=1280,height=860,left=100,top=60'
  //   );
  //  // console.log('[Support] window.open result=', w);
  //   if (!w) { console.warn('[Support] Popup blocked'); return; }

  //   const timeout = setTimeout(() => { console.warn('[Support] Timeout — no AI_TICKET_READY received'); window.removeEventListener('message', handler); }, 15000);

  //   const handler = (event: MessageEvent) => {
  //     //console.log('[Support] message received', event.origin, event.data?.type);
  //     if (event.source !== w || event.data?.type !== 'AI_TICKET_READY') return;
  //     clearTimeout(timeout);
  //     window.removeEventListener('message', handler);

  //     const user   = this.authService.currentUser;
  //     const ctx    = this.supportCtx.getContext();
  //     const oauth2 = JSON.parse(localStorage.getItem('oauth2') || '{}');

  //     const payload = {
  //       type:        'AI_TICKET_META',
  //       accessToken: oauth2.access_token ?? '',
  //       userInfo: {
  //         name:  user?.name  ?? '',
  //         email: user?.email ?? '',
  //       },
  //       origin:   ctx.origin   ?? 'Pinnacle SiteReview',
  //       category: ctx.category ?? '',
  //       tags:     ctx.tags     ?? [],
  //       meta: {
  //         'User':       user?.name              ?? '',
  //         'Email':      user?.email             ?? '',
  //         'Role':       user?.userLevel?.name   ?? '',
  //         'Page URL':   window.location.href,
  //         'Page Title': this.pageTitle,
  //         ...ctx.meta,
  //       }
  //     };
  //     console.log('Posting support ticket context to support window', payload);
  //     w.postMessage(payload, this.SUPPORT_BASE);
  //   };

  //   window.addEventListener('message', handler);
  // }

  changePageTitle() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((routeChange) => {
      var routeParts = this.routePartsService.generateRouteParts(this.activeRoute.snapshot);
      if (!routeParts.length)
        return this.title.setTitle(this.appTitle);
      // Extract title from parts;
      this.pageTitle = routeParts
        .reverse()
        .map((part) => part.title)
        .reduce((partA, partI) => { return `${partA} > ${partI}` });
      this.pageTitle += ` | ${this.appTitle}`;
      this.title.setTitle(this.pageTitle);

    });
 
  }
}
